import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import { FaMoneyBillWave, FaSave, FaExclamationCircle, FaSpinner } from 'react-icons/fa';
import { ENDPOINT } from '../api/auth';
import { useAuth } from '../context/AuthContext';
import './InstitutionalSpending.css';

// Create axios instance for API calls
const axiosInstance = axios.create();

const InstitutionalSpending = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    amount: '',
    category: 'institution',
    reason: '',
    comment: '',
    payment_method: 'cash',
    bank_name: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Dropdown options
  const reasonOptions = [
    { value: 'electric bill', label: 'Electric Bill' },
    { value: 'rental', label: 'Rental' },
  ];
  
  const paymentMethodOptions = [
    { value: 'cash', label: 'Cash' },
    { value: 'bank', label: 'Bank' },
  ];
  
  const bankOptions = [
    { value: 'Awash', label: 'Awash' },
    { value: 'CBE', label: 'CBE' },
    { value: 'Dashen', label: 'Dashen' },
    { value: 'Abyssinia', label: 'Abyssinia' },
    { value: 'Other', label: 'Other' },
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSelectChange = (name, selectedOption) => {
    setFormData(prev => ({
      ...prev,
      [name]: selectedOption ? selectedOption.value : ''
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    
    if (!formData.reason) {
      toast.error('Please select a reason for the spending');
      return;
    }
    
    if (formData.payment_method === 'bank' && !formData.bank_name) {
      toast.error('Please select a bank for bank payments');
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      const token = localStorage.getItem('token');
      const headers = {
        'Content-Type': 'application/json',
        ...(token && { 'Authorization': `Bearer ${token}` })
      };

      const payload = {
        ...formData,
        amount: parseFloat(formData.amount),
        user_id: user?.id || user?.user_id, // Handle both user object formats
      };

      const response = await axiosInstance.post(
        ENDPOINT,
        payload,
        {
          params: { action: 'add_car_spending' },
          headers
        }
      );

      if (response.data.success) {
        // Reset form
        setFormData({
          amount: '',
          category: 'institution',
          reason: '',
          comment: '',
          payment_method: 'cash',
          bank_name: ''
        });
        
        toast.success('Institutional spending recorded successfully!');
      } else {
        throw new Error(response.data.message || 'Failed to record spending');
      }
    } catch (error) {
      console.error('Error recording institutional spending:', error);
      const errorMessage = error.response?.data?.message || error.message || 'An error occurred while recording spending';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="institutional-spending">
      <div className="spending-card">
        <h2 className="spending-title">
          <FaMoneyBillWave className="title-icon" />
          Record Institutional Spending
        </h2>
        
        {error && (
          <div className="error-message">
            <FaExclamationCircle />
            <span>{error}</span>
            <button 
              className="retry-button"
              onClick={handleSubmit}
              disabled={loading}
            >
              {loading ? 'Retrying...' : 'Retry'}
            </button>
          </div>
        )}
        
        <form onSubmit={handleSubmit} className="spending-form">
          <div className="form-group">
            <label htmlFor="amount">Amount (ብር)</label>
            <div className="input-with-icon">
              <input
                type="number"
                id="amount"
                name="amount"
                value={formData.amount}
                onChange={handleChange}
                className="form-control"
                placeholder="0.00"
                step="0.01"
                min="0.01"
                required
              />
              <FaMoneyBillWave className="input-icon" />
            </div>
          </div>
          
          <div className="form-group">
            <label htmlFor="reason">Reason</label>
            <select
              id="reason"
              name="reason"
              value={formData.reason}
              onChange={(e) => handleSelectChange('reason', { value: e.target.value })}
              className="form-control"
              required
            >
              <option value="">Select a reason</option>
              {reasonOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          
          <div className="form-group">
            <label htmlFor="payment_method">Payment Method</label>
            <select
              id="payment_method"
              name="payment_method"
              value={formData.payment_method}
              onChange={(e) => handleSelectChange('payment_method', { value: e.target.value })}
              className="form-control"
            >
              {paymentMethodOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          
          {formData.payment_method === 'bank' && (
            <div className="form-group">
              <label htmlFor="bank_name">Bank Name</label>
              <select
                id="bank_name"
                name="bank_name"
                value={formData.bank_name}
                onChange={(e) => handleSelectChange('bank_name', { value: e.target.value })}
                className="form-control"
                required={formData.payment_method === 'bank'}
              >
                <option value="">Select a bank</option>
                {bankOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          )}
          
          <div className="form-group">
            <label htmlFor="comment">Comment (Optional)</label>
            <textarea
              id="comment"
              name="comment"
              value={formData.comment}
              onChange={handleChange}
              className="form-control"
              rows="3"
              placeholder="Add any additional notes here..."
            />
          </div>
          
          <button 
            type="submit" 
            className="submit-button"
            disabled={loading}
          >
            {loading ? (
              <>
                <FaSpinner className="spinner" />
                Recording...
              </>
            ) : (
              <>
                <FaSave className="button-icon" />
                Record Spending
              </>
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

export default InstitutionalSpending;
